import { Button, TextInput } from '@carbon/react';
import { Copy } from '@carbon/icons-react';
import { asProblem, detailRoute, FormModal, hasPermission, problemMessage, useAdmin, type RecordValues } from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { hoApi } from '../api';

/** What can be copied: its resource, its copy endpoint, the permission to copy it and what the dialog says. */
const KINDS = {
  'menu.menu': { path: '/api/v1/menu/menus/{id}/duplicate', permission: 'ho.menu.menus.manage', hint: 'duplicate.menuHint' },
  'menu.price_list': { path: '/api/v1/menu/price-lists/{id}/duplicate', permission: 'ho.menu.price_lists.manage', hint: 'duplicate.priceListHint' },
  'config.pos_profile': { path: '/api/v1/menu/config/pos-profiles/{id}/duplicate', permission: 'ho.config.pos_profiles.manage', hint: 'duplicate.posProfileHint' },
  'org.role': { path: '/api/v1/identity/roles/{id}/duplicate', permission: 'ho.org.roles.manage', hint: 'duplicate.roleHint' },
} as const satisfies Record<string, { path: string; permission: string; hint: I18nKey }>;

/** Code length the API takes (the generated code fields' limit). */
const MAX_CODE = 50;

/**
 * Copies a menu, a price list, a POS profile or a role under a new code and opens the copy, as a starting point for a store's or a
 * region's own. The copy applies nowhere until it is assigned (a menu or price list) or put on terminals and synced (a profile).
 */
export function DuplicateButton({ resource, record }: { resource: keyof typeof KINDS; record: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const navigate = useNavigate();
  const kind = KINDS[resource];
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  const copy = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST(kind.path, {
        params: { path: { id: record.id } },
        body: { code: code.trim(), name: name.trim() },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (created) => {
      setOpen(false);
      void navigate(detailRoute(resource, String(created.id)));
    },
  });

  if (!hasPermission(session, kind.permission)) return null;

  const start = () => {
    const original = String(record.code ?? '');
    setCode(`${original}-COPY`.slice(0, MAX_CODE));
    setName(t('duplicate.defaultName', { name: String(record.name ?? '') }));
    copy.reset();
    setOpen(true);
  };

  // A problem about the code or the name shows on that field; anything else above the form.
  const problem = copy.error ? (asProblem(copy.error) as ApiProblem) : null;
  const fieldError = (key: 'code' | 'name') => {
    const byName = Object.entries(problem?.errors ?? {}).find(([field]) => field.toLowerCase() === key)?.[1]?.[0];
    return byName ?? (problem?.field === key ? problemMessage(t, problem) : undefined);
  };
  const general = problem && !problem.errors && !problem.field ? problemMessage(t, problem) : null;

  return (
    <>
      <Button kind="tertiary" renderIcon={Copy} onClick={start}>
        {t('duplicate.action')}
      </Button>
      {open && (
        <FormModal
          title={t('duplicate.title', { name: `${String(record.code ?? '')} – ${String(record.name ?? '')}` })}
          label={t('duplicate.action')}
          size="sm"
          primaryText={t('duplicate.confirm')}
          pending={copy.isPending}
          error={general}
          onSubmit={() => copy.mutate()}
          onClose={() => setOpen(false)}
        >
          <p>{t(kind.hint)}</p>
          <TextInput
            id="duplicate-code"
            labelText={t('md.fields.code')}
            required
            maxLength={MAX_CODE}
            value={code}
            invalid={Boolean(fieldError('code'))}
            invalidText={fieldError('code')}
            onChange={(e) => setCode(e.target.value)}
          />
          <TextInput
            id="duplicate-name"
            labelText={t('md.fields.name')}
            required
            value={name}
            invalid={Boolean(fieldError('name'))}
            invalidText={fieldError('name')}
            onChange={(e) => setName(e.target.value)}
          />
        </FormModal>
      )}
    </>
  );
}
